import { TFunction } from 'i18next';
import { GoogleSpace } from '../types/employee.type';
import { TaskListOption, ThreadMessage } from '../types/task.type';

const PERSONAL_KEY = '__personal__';

function spaceKey(name: string | null | undefined): string {
  return name?.trim() || PERSONAL_KEY;
}

export function mergeTaskListOptions(
  apiLists: TaskListOption[],
  employeeSpaces: GoogleSpace[] = [],
  knownSpaceNames: string[] = [],
): TaskListOption[] {
  const map = new Map<string, TaskListOption>();

  for (const opt of apiLists) {
    map.set(spaceKey(opt.space_name), opt);
  }

  for (const space of employeeSpaces) {
    const name = space.space_name?.trim();
    if (!name) continue;
    if (!map.has(name)) {
      map.set(name, {
        task_list_id: `space:${name}`,
        task_list_name: name,
        space_name: name,
      });
    }
  }

  for (const name of knownSpaceNames) {
    const trimmed = name?.trim();
    if (!trimmed) continue;
    if (!map.has(trimmed)) {
      map.set(trimmed, {
        task_list_id: `space:${trimmed}`,
        task_list_name: trimmed,
        space_name: trimmed,
      });
    }
  }

  const personal = map.get(PERSONAL_KEY)
    ?? apiLists.find(o => !o.space_name)
    ?? null;

  const spaces = Array.from(map.entries())
    .filter(([key]) => key !== PERSONAL_KEY)
    .map(([, opt]) => opt)
    .sort((a, b) => (a.space_name ?? '').localeCompare(b.space_name ?? ''));

  if (personal) return [personal, ...spaces];
  return spaces.length > 0 ? spaces : apiLists;
}

export function parseTaskListsResponse(res: unknown): TaskListOption[] {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (typeof res === 'object') {
    const obj = res as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as TaskListOption[];
    if (Array.isArray(obj.items)) return obj.items as TaskListOption[];
    if (Array.isArray(obj.lists)) return obj.lists as TaskListOption[];
  }
  return [];
}

export function dateInputToRfc3339(dateStr: string): string | undefined {
  if (!dateStr) return undefined;
  return `${dateStr}T00:00:00.000Z`;
}

export function rfc3339ToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export function getTaskListLabel(option: TaskListOption, t: TFunction): string {
  if (option.space_name) return option.space_name;
  if (option.task_list_name && option.task_list_name !== 'My Tasks') {
    return option.task_list_name;
  }
  return t('tasks.personalTasks');
}

export function getApiErrorDetail(error: unknown, fallback = 'Error'): string {
  const err = error as { response?: { data?: { detail?: string } }; message?: string };
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (typeof err?.message === 'string' && err.message.trim()) return err.message;
  return fallback;
}

export function normalizeThreadMessages(raw: unknown): ThreadMessage[] | null {
  if (!raw) return null;
  if (!Array.isArray(raw)) return null;
  const messages = raw
    .map((item): ThreadMessage | null => {
      if (!item || typeof item !== 'object') return null;
      const msg = item as Record<string, unknown>;
      const messageId = msg.message_id ?? msg.messageId;
      const senderName = msg.sender_name ?? msg.senderName;
      const senderType = msg.sender_type ?? msg.senderType;
      const text = msg.text;
      const createTime = msg.create_time ?? msg.createTime;
      if (
        typeof messageId !== 'string'
        || typeof senderName !== 'string'
        || typeof text !== 'string'
        || typeof createTime !== 'string'
      ) {
        return null;
      }
      const normalizedType = senderType === 'BOT' ? 'BOT' : 'HUMAN';
      return {
        message_id: messageId,
        sender_name: senderName,
        sender_type: normalizedType,
        sender_avatar_url: typeof msg.sender_avatar_url === 'string'
          ? msg.sender_avatar_url
          : typeof msg.senderAvatarUrl === 'string'
            ? msg.senderAvatarUrl
            : null,
        text,
        create_time: createTime,
      };
    })
    .filter((msg): msg is ThreadMessage => msg !== null);
  return messages.length > 0 ? messages : null;
}

export function getKnownSpaceNamesForEmployee(
  tasks: { employee_id: string; space_name: string | null }[],
  employeeId: string,
): string[] {
  const names = new Set<string>();
  for (const task of tasks) {
    if (task.employee_id === employeeId && task.space_name?.trim()) {
      names.add(task.space_name.trim());
    }
  }
  return Array.from(names);
}
